import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';

import viewInventory from '@/shared/api/inventory/viewInventory';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import { InventoryGetModel } from '@/shared/types/inventory.type';
import Product from '@/shared/types/products/product.type';
import ProductVariant from '@/shared/types/products/productVariant.type';

import InventoryTableRow from '../../../types/inventoryTableRow.type';

type Args = {
  initialData?: InventoryTableRow;
  product: Product | null;
};

const useMapInventoryToProductDialog = ({ initialData, product }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    null,
  );
  const [selectedInventory, setSelectedInventory] =
    useState<InventoryGetModel | null>(null);

  const [isSellOutOfStock, setIsSellOutOfStock] = useState(false);

  const [inventoryList, setInventoryList] = useState<
    InventoryGetModel[] | null
  >(null);

  const [inventorySearchValue, setInventorySearchValue] = useState('');
  const [inventorySearchValueReason, setInventorySearchValueReason] =
    useState('');

  const inventorySearchValueDebounced = useDebounce(inventorySearchValue, 500);

  const [dataIsLoading, setDataIsLoading] = useState(false);

  const onInventorySearchValueChange = (value: string, reason: string) => {
    setInventorySearchValueReason(reason);
    setInventorySearchValue(value);
  };

  useEffect(() => {
    if (initialData && product && inventoryList) {
      const selectedInventoryItem = inventoryList.find(
        (item) => item.inventory_id === initialData.inventory_id,
      );
      const selectedVariantItem = product.variants.find(
        (item) => item.variant_id === initialData.variant_id,
      );

      setSelectedInventory(selectedInventoryItem || null);
      setSelectedVariant(selectedVariantItem || null);
    }
  }, [initialData, product, inventoryList]);

  useEffect(() => {
    const fetchInventory = async () => {
      setDataIsLoading(true);

      const columns: Partial<keyof InventoryGetModel>[] = [
        'inventory_description',
        'inventory_name',
        'inventory_id',
        'available',
        'reserved',
        'total',
      ];

      const response = await viewInventory(
        {
          page_size: 50,
          search: columns.map((item, index) => ({
            [item]: {
              cop: ['ilike'],
              value: inventorySearchValueDebounced,
              op: index > 0 ? 'or' : undefined,
            },
          })),
        },
        redirectLogin,
      );

      if (response.success) {
        setInventoryList(response.data);
      }
      setDataIsLoading(false);
    };

    if (!inventorySearchValueReason || inventorySearchValueReason !== 'reset') {
      void fetchInventory();
    }
  }, [
    redirectLogin,
    inventorySearchValueDebounced,
    inventorySearchValueReason,
  ]);

  return {
    dataIsLoading,
    inventoryList,
    selectedVariant,
    isSellOutOfStock,
    selectedInventory,
    inventorySearchValue,
    onInventorySearchValueChange,
    setSelectedVariant,
    setIsSellOutOfStock,
    setSelectedInventory,
  };
};

export default useMapInventoryToProductDialog;
